import React, { useEffect, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import Webcam from "react-webcam";
import { style } from "@mui/system";
import styles from "./index.module.scss";
import { Button } from "../Button";
import reRecordButton from "../../../assets/images/re-record.png";

interface VideoRecorderProps {
  recordedVideoBlob: Blob | null;
  setRecordedVideoBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
  onFileDataUpload?: (file: File) => void;
  openVideoRecorder?: boolean;
}

const VideoRecorder: React.FC<VideoRecorderProps> = ({
  recordedVideoBlob,
  setRecordedVideoBlob,
  setIsValid,
  onFileDataUpload,
  openVideoRecorder = false,
}) => {
  // Private fields use underscore for user instruction
  // const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  // const [recordedVideoBlob, setRecordedVideoBlob] = useState<Blob | null>(null);
  // const [isValid, setIsValid] = useState(false);
  const _webcamRef = useRef<Webcam>(null);
  const _mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const _chunksRef = useRef<Blob[]>([]);
  const _timerRef = useRef<NodeJS.Timeout>();
  let recordTimer = useRef<NodeJS.Timeout | null>(null);

  const [showWebcam, setShowWebcam] = useState(true);
  const [recordedVideo, setRecordedVideo] = useState<string | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<
    "idle" | "recording" | "recorded"
  >("idle");
  const [timeLeft, setTimeLeft] = useState<number>(112);
  const [mimeType, setMimeType] = useState("");
  const [cameraPermission, setCameraPermission] = useState<string>("granted");
  const [audioPermission, setAudioPermission] = useState<string>("granted");
  const [isPaused, setIsPaused] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setIsModalOpen(openVideoRecorder);
    console.log("VideoRecorder component rendered", openVideoRecorder);
  }, []);

  useEffect(() => {
    setMimeType(getSupportedMimeType());
    setRecordedVideoBlob(null);
    setRecordedVideo(null);
    setRecordingStatus("idle");

    const checkPermissions = async () => {
      const cameraStatus = await checkCameraPermission();
      const audioStatus = await checkAudioPermission();
      setCameraPermission(cameraStatus);
      setAudioPermission(audioStatus);
    };

    checkPermissions();

    return () => {
      if (_timerRef.current) {
        clearInterval(_timerRef.current);
      }
      console.log("Cleaning up VideoRecorder component and stopping recording");
      stopRecording();
    };
  }, [isModalOpen]);

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const getSupportedMimeType = (): string => {
    if (isSafari || isMobile) {
      return "video/mp4";
    }
    const supportedTypes = [
      'video/webm; codecs="vp9,opus"',
      'video/webm; codecs="vp8,opus"',
      "video/webm",
      'video/mp4; codecs="avc1,aac"',
    ];
    for (const type of supportedTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "";
  };

  const startTimer = () => {
    setTimeLeft(112);
    _timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (_timerRef.current) {
      clearInterval(_timerRef.current);
    }
  };

  const handleStartRecording = () => {
    setIsValid(true);

    if (cameraPermission === "denied" && audioPermission === "denied") {
      alert("Both camera and audio are disabled. Please allow access to both.");
    } else if (cameraPermission === "denied") {
      alert(
        "Camera access is blocked. Please allow access in your browser settings.",
      );
    } else if (audioPermission === "denied") {
      alert(
        "Audio access is blocked. Please allow access in your browser settings.",
      );
    } else {
      startRecording();
    }
  };

  const startRecording = () => {
    _chunksRef.current = [];
    _mediaRecorderRef.current?.stop();
    _mediaRecorderRef.current = null;
    stopRecordTimer();
    setTimeout(() => {
      const stream = _webcamRef.current?.video?.srcObject as MediaStream;
      if (!stream) {
        console.error("No stream available");
        return;
      }
      const options: MediaRecorderOptions = {
        mimeType,
        videoBitsPerSecond: 1500000,
        audioBitsPerSecond: 64000,
      };
      try {
        const mediaRecorder = new MediaRecorder(stream, options);
        _mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            _chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(_chunksRef.current, { type: mimeType });
          const url = URL.createObjectURL(blob);
          setRecordedVideoBlob(blob);
          setRecordedVideo(url);
          setShowWebcam(false);
        };

        mediaRecorder.start();
        setRecordingStatus("recording");
        startTimer();

        recordTimer.current = setTimeout(() => {
          if (_mediaRecorderRef.current?.state === "recording") {
            console.log(
              "Stopping recording due to timeout",
              _mediaRecorderRef.current?.state,
            );
            stopRecording();
          }
        }, 111200);
      } catch (error) {
        console.error("MediaRecorder error:", error);
      }
    }, 500);
  };

  const stopRecording = () => {
    if (_mediaRecorderRef.current?.state === "recording") {
      _mediaRecorderRef.current.stop();
      stopTimer();
      setRecordingStatus("recorded");
      stopRecording();
    }
    console.log("Recording stopped");
    stopRecordTimer();
  };

  const stopRecordTimer = () => {
    if (recordTimer.current) {
      clearTimeout(recordTimer.current);
      recordTimer.current = null;
    }
  };

  const handleReRecord = () => {
    setRecordedVideo(null);
    setRecordedVideoBlob(null);
    setRecordingStatus("idle");
    setTimeLeft(112);
    _chunksRef.current = [];
    setShowWebcam(true);
    _mediaRecorderRef.current?.stop();
    _mediaRecorderRef.current = null;
  };

  const videoConstraints = {
    width: { ideal: 280 },
    height: { ideal: 250 },
    facingMode: "user",
    aspectRatio: 3 / 4,
  };

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      return result.state ?? "granted";
    } catch (error) {
      console.error("Error checking camera permission:", error);
      return "granted";
    }
  };

  const checkAudioPermission = async () => {
    try {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return result.state ?? "granted";
    } catch (error) {
      console.error("Error checking audio permission:", error);
      return "granted";
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current
          .play()
          .catch((error) => console.log("Playback failed:", error));
        setIsPaused(false);
      } else {
        videoRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = async () => {
    // First stop recording and wait for it to complete
    if (_mediaRecorderRef.current?.state === "recording") {
      await new Promise<void>((resolve) => {
        if (_mediaRecorderRef.current) {
          _mediaRecorderRef.current.onstop = () => {
            stopTimer();
            stopRecordTimer();
            resolve();
          };
          _mediaRecorderRef.current.stop();
        } else {
          resolve();
        }
      });
    }

    // Stop all media tracks
    const stream = _webcamRef.current?.video?.srcObject as MediaStream;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }

    // Reset all states after recording is stopped
    setRecordedVideoBlob(null);
    setRecordedVideo(null);
    setRecordingStatus("idle");
    setTimeLeft(112);
    _chunksRef.current = [];
    setShowWebcam(true);
    setIsValid(false);
    setIsModalOpen(false);
    _mediaRecorderRef.current = null;
  };

  const handleSubmitRecordedVideo = async () => {
    console.log("Submitting recorded video:", recordedVideoBlob);
    console.log("Recorded video URL:", recordedVideo);
    if (recordedVideo) {
      const response = await fetch(recordedVideo);
      const recordedVideoBlobForAWS = await response.blob();
      const file = new File([recordedVideoBlobForAWS], "recorded-video.mp4", {
        type: recordedVideoBlobForAWS.type,
      });
      if (onFileDataUpload) {
        onFileDataUpload(file);
      }
    } else {
      alert("Please record a video before submitting.");
    }
    handleModalClose();
  };

  return (
    <div>
      <div className={styles.recordButton} onClick={handleModalOpen}>
        <div className={styles.recordButtonIcon}></div>
        <div className={styles.recordButtonText}>Record Video</div>
      </div>
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        className={styles.modal}
      >
        <div className={styles.modalBox}>
          <div>
            {showWebcam && (
              <div className={styles.webcamWrapper}>
                <Webcam
                  ref={_webcamRef}
                  audio={true}
                  videoConstraints={videoConstraints}
                  muted
                  mirrored
                  className={styles.webcamElement}
                />
              </div>
            )}

            {recordedVideo && !showWebcam && (
              <div className={styles.videoPreviewBox}>
                <video
                  ref={videoRef}
                  controlsList="nodownload"
                  preload="metadata"
                  controls
                  muted={true}
                  playsInline
                  onPause={() => setIsPaused(true)}
                  onPlay={() => setIsPaused(false)}
                  className={styles.videoElement}
                >
                  <source
                    src={recordedVideo}
                    type={recordedVideoBlob?.type || "video/webm"}
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}
          </div>

          <div className={styles.controlsContainer}>
            <div className={styles.buttonContainer}>
              {recordingStatus === "idle" && recordedVideoBlob === null && (
                <div role="button" tabIndex={0}>
                  <div
                    onClick={handleStartRecording}
                    className={styles.recordButton}
                  >
                    <div className={styles.recordButtonIcon}></div>
                    <span className={styles.recordButtonText}>record now</span>
                  </div>
                </div>
              )}

              {recordingStatus === "recording" && (
                <div role="button" tabIndex={1}>
                  <div onClick={stopRecording} className={styles.recordButton}>
                    <div className={styles.stopButtonIcon}></div>
                    <span className={styles.recordButtonText}>
                      stop recording
                    </span>
                  </div>
                </div>
              )}

              {recordedVideo && recordingStatus === "recorded" && (
                <div role="button" tabIndex={2}>
                  <div
                    onClick={handleReRecord}
                    className={styles.reRecordButton}
                  >
                    <div className={styles.reRecordButtonIcon}>
                      <img src={reRecordButton} alt="Re-record" />
                    </div>
                    <span className={styles.recordButtonText}>re-record</span>
                  </div>
                </div>
              )}
            </div>

            <div
              className={`${styles.timerContainer} ${recordingStatus === "recording" ? "" : styles.hidden}`}
            >
              {`${timeLeft - 1} seconds left`}
            </div>
          </div>
          <div className={styles.footerContainer}>
            <div className={styles.actionButtonsContainer}>
              <button
                onClick={handleModalClose}
                className={styles.secondaryButton}
              >
                discard
              </button>
              <Button
                onClick={handleSubmitRecordedVideo}
                buttonClassName={styles.primaryButton}
                disable={recordingStatus !== "recorded"}
              >
                submit
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VideoRecorder;
